import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api-client";

export interface ExportPayloadState {
  content: string;
  contentType: string;
  truncated: boolean;
  sizeBytes: number | null;
  blob: Blob;
  /** Created once per load() — reused across renders so we don't leak a
   *  fresh blob: URL (and re-encode the content) on every render. */
  objectUrl: string;
}

/**
 * E-027 — lazily fetches + caches a persisted export payload's text content
 * by record id, for the Export History detail-row preview/download.
 * `payloads` is keyed by exportRecordId; a cached entry is never re-fetched.
 */
export function useExportPayload() {
  const [payloads, setPayloads] = useState<Record<string, ExportPayloadState | undefined>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);
  // Guards against a second load() firing while the first is still in flight
  // (e.g. rapid double-click) before the payloads-state cache check would see it.
  const inFlight = useRef<Set<string>>(new Set());
  // Every object URL we've handed out, so we can revoke them on unmount — a
  // blob: URL pins its Blob in memory until revoked, and these are never
  // otherwise released for the life of the tab.
  const createdUrls = useRef<Set<string>>(new Set());

  const load = useCallback(
    async (recordId: string) => {
      if (payloads[recordId] || inFlight.current.has(recordId)) return;
      inFlight.current.add(recordId);
      setLoadingId(recordId);
      setErrorId(null);
      try {
        const { blob, contentType, headers } = await api.getBlob(`/export-records/${recordId}/payload`);
        const content = await blob.text();
        const sizeBytesHeader = headers.get("x-payload-size-bytes");
        const objectUrl = URL.createObjectURL(blob);
        createdUrls.current.add(objectUrl);
        setPayloads((prev) => ({
          ...prev,
          [recordId]: {
            content,
            contentType,
            blob,
            truncated: headers.get("x-payload-truncated") === "true",
            sizeBytes: sizeBytesHeader != null ? Number(sizeBytesHeader) : null,
            objectUrl,
          },
        }));
      } catch {
        setErrorId(recordId);
      } finally {
        inFlight.current.delete(recordId);
        setLoadingId(null);
      }
    },
    [payloads],
  );

  // Revoke all object URLs on unmount. The ref is read in the cleanup so it
  // sees every URL created over the hook's lifetime (mount-once effect).
  useEffect(() => {
    const urls = createdUrls.current;
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
      urls.clear();
    };
  }, []);

  return { payloads, loadingId, errorId, load };
}
